import './XeroLineItemsSectionFooterLegacy.scss';

import { intl, numberHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning, WarningIndicator } from 'modules/request';
import { memo } from 'react';

import { LineItemFooterLine } from '../../../../components/lineItemsSection/LineItemFooterLine/LineItemFooterLine';
import LineItemFooterSeparator from '../../../../components/lineItemsSection/LineItemFooterSeparator';
import { getXeroContext, getXeroLineItems, getXeroLineItemsSummary } from '../../../../selectors/xero';
import XeroCisDeductionLegacy from '../XeroCisDeductionLegacy/XeroCisDeductionLegacy';
import { messages } from './XeroLineItemsSectionFooterLegacy.messages';
import { LineItemsSummaryLegacyProps } from './XeroLineItemsSectionFooterLegacy.types';

/**
 * @deprecated Use redesigned component {@link XeroLineItemsSectionFooter}
 */
export const XeroLineItemsSectionFooterLegacy = memo<LineItemsSummaryLegacyProps>((props) => {
    const { bem, qa, request, ocrData } = props;

    const { details, currency, companyId } = request;
    const { lineAmountType } = details;

    const templateName = selectors.template.getTemplateDisplayNameByCode(request.integrationCode);

    const summary = useSelector((state) => getXeroLineItemsSummary(state, request));
    const lineItems = useSelector((state) => getXeroLineItems(state, request));
    const xeroContext = useSelector(getXeroContext);

    const showSubtotalWarning = Boolean(
        ocrData?.document?.subTotal && summary.subtotalAmount !== ocrData.document?.subTotal
    );

    const totalTax = summary.taxInfo.reduce<number>(
        (previousValue, currentValue) => previousValue + currentValue.taxAmount,
        0
    );

    const showTaxTotalWarning =
        numberHelpers.isNumber(ocrData?.document?.totalTax) && totalTax !== ocrData.document?.totalTax;

    const showTotalWarning = Boolean(ocrData?.document?.total && summary.totalAmount !== ocrData.document?.total);

    return (
        <div className={bem('footer')}>
            <div className={bem('footer-amount-mismatch')}>
                {summary.amountMismatches.map((errorText) => (
                    <div
                        key={errorText}
                        className={bem('footer-amount-mismatch-item')}
                        data-qa={bem('footer-amount-mismatch-item')}
                    >
                        {errorText}
                    </div>
                ))}
            </div>

            <div className={bem('footer-lines')}>
                <WarningIndicator
                    show={showSubtotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={summary.subtotalAmount}
                            value2={ocrData?.document?.subTotal}
                            label1={messages.differentSubtotalWarningLabel1({ templateName })}
                            label2={messages.differentSubtotalWarningLabel2}
                            description={messages.differentSubtotalWarningDescription({ templateName })}
                        />
                    }
                >
                    <LineItemFooterLine
                        warning={showSubtotalWarning}
                        label={messages.footerSubtotalText}
                        qa={qa('subtotal-footer-line')}
                    >
                        {intl.formatCurrency(summary.subtotalAmount, currency)}
                    </LineItemFooterLine>
                </WarningIndicator>

                {lineAmountType !== domain.LineAmountType.NoTax &&
                    summary.taxInfo.map((taxItem, index) => {
                        if (index === summary.taxInfo.length - 1) {
                            return (
                                <WarningIndicator
                                    show={showTaxTotalWarning}
                                    content={
                                        <TwoDifferentValuesWarning
                                            value1={totalTax}
                                            value2={ocrData?.document?.totalTax}
                                            label1={messages.differentTaxTotalWarningLabel1({ templateName })}
                                            label2={messages.differentTaxTotalWarningLabel2}
                                            description={messages.differentTaxTotalWarningDescription({ templateName })}
                                        />
                                    }
                                >
                                    <LineItemFooterLine
                                        warning={showTaxTotalWarning}
                                        key={taxItem.taxRateString}
                                        label={`${taxItem.taxRateString}:`}
                                        qa={qa('tax-component-footer-line')}
                                    >
                                        {intl.formatCurrency(taxItem.taxAmount, currency)}
                                    </LineItemFooterLine>
                                </WarningIndicator>
                            );
                        }

                        return (
                            <LineItemFooterLine
                                key={taxItem.taxRateString}
                                label={`${taxItem.taxRateString}:`}
                                qa={qa('tax-component-footer-line')}
                            >
                                {intl.formatCurrency(taxItem.taxAmount, currency)}
                            </LineItemFooterLine>
                        );
                    })}

                {Boolean(summary.taxAdjustmentsAmount) && (
                    <LineItemFooterLine label={messages.taxAdjustmentsTextLabel} qa={qa('tax-adjustment-footer-line')}>
                        {intl.formatCurrency(summary.taxAdjustmentsAmount, currency)}
                    </LineItemFooterLine>
                )}

                <LineItemFooterSeparator />

                <WarningIndicator
                    show={showTotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={summary.totalAmount}
                            value2={ocrData?.document?.total}
                            label1={messages.differentTotalWarningLabel1({ templateName })}
                            label2={messages.differentTotalWarningLabel2}
                            description={messages.differentTotalWarningDescription({ templateName })}
                        />
                    }
                >
                    <LineItemFooterLine warning={showTotalWarning} label={messages.footerTotalText}>
                        <div className={bem('footer-total-line')} data-qa={qa('total-footer-line')}>
                            {intl.formatCurrency(summary.totalAmount, currency)}
                        </div>
                    </LineItemFooterLine>
                </WarningIndicator>

                {(details.integrationCode === domain.IntegrationCode.XeroBill ||
                    details.integrationCode === domain.IntegrationCode.XeroInvoice) && (
                    <XeroCisDeductionLegacy
                        companyId={companyId}
                        details={details}
                        organizationCisSettings={xeroContext.cisSettings}
                        lineItems={lineItems}
                        currency={currency}
                    />
                )}
            </div>
        </div>
    );
});

XeroLineItemsSectionFooterLegacy.displayName = 'XeroLineItemsSectionFooterLegacy';
